/**
 * LiveTradePanel — Real-Time In-Progress Trade Journaling Surface
 *
 * Phase 3 foundation: surfaces an always-visible journaling panel inside the
 * open trade card so traders can capture their thesis / emotions / confluences
 * *while the trade is live*, not hours later during EOD review.
 *
 * Design principles
 * -----------------
 * 1. Low friction: a plaintext textarea with debounced autosave (800ms idle)
 *    so the user can type and keep watching the chart — never blocks a save.
 * 2. Progress-at-a-glance: three pips (Setup · Confluences · Notes) show
 *    what's captured and what's still missing, without nagging.
 * 3. Quick confluence chips: surfaces the user's top-N historical confluences
 *    so a single click tags the live trade. Pulled from `allTrades` so it
 *    evolves with the trader's own patterns.
 * 4. Escape hatch: a "Full Editor" button routes to the existing
 *    AddEditTradeModal whenever the trader needs richer editing
 *    (ATM overrides, mistakes, rich-text formatting, etc.).
 *
 * Only mounted inside an open trade card (`trade.isOpen === true`). Unmounts
 * as soon as exits complete and the trade closes — all state (notes drafts,
 * timers) tears down cleanly.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, Edit3, Check, Pencil, ArrowUpRight } from 'lucide-react';
import { computeLiveInsights } from '../../services/coach/liveInsights';
import { barDataService } from '../../services/barData';
import { gapStraddlesTimestamp } from '../../services/barData/gapDetect';
import LiveTradeInsights from './LiveTradeInsights';
import { getEtParts, getExecutionInstantMs } from '../../services/timeService';
import { looksLikeHtml, notesHtmlToPlain, plainTextToNotesHtml } from '../../utils/notesHtml';

/* ------------------------------------------------------------------ *
 * Elapsed-time helpers
 * ------------------------------------------------------------------ */

// Parse trade.entryTime + trade.date into a real Date, returning elapsed ms.
// entryTime formats we support (same as eventAutoTag.js):
//   "HH:MM", "HH:MM:SS" (24h)
//   "H:MM AM/PM", "HH:MM:SS AM/PM"
//   "MM/DD/YYYY HH:MM AM/PM", "YYYY-MM-DD HH:MM"  (ignore the date half — use trade.date)
// Returns null when we can't reasonably parse or the result is nonsensical.
const computeElapsedMs = (trade) => {
  if (!trade || !trade.entryTime) return null;
  const datePart = trade.date || new Date().toISOString().slice(0, 10);

  // Strip any leading date portion so only the time remains
  let timeStr = String(trade.entryTime).trim();
  if (timeStr.includes('/')) {
    const parts = timeStr.split(/\s+/);
    // "MM/DD/YYYY HH:MM AM/PM" -> take from index 1 on
    timeStr = parts.slice(1).join(' ');
  } else if (timeStr.includes('T')) {
    timeStr = timeStr.split('T')[1] || '';
  }

  const m = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = parseInt(m[2], 10);
  const seconds = parseInt(m[3] || '0', 10);
  const period = m[4]?.toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  // Combine with trade.date in local time (ISO-like but without Z so local TZ applies)
  const iso = `${datePart}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  const entryMs = new Date(iso).getTime();
  if (Number.isNaN(entryMs)) return null;

  const elapsed = Date.now() - entryMs;
  // Guard: a negative value means clock skew or bad data — treat as "just opened"
  if (elapsed < 0) return 0;
  // Guard: if the "elapsed" is absurdly large (>36h), the trade.date is probably stale
  // or we parsed AM/PM wrong. Return null so the UI falls back to a plain "Live" label.
  if (elapsed > 36 * 60 * 60 * 1000) return null;
  return elapsed;
};

// Format elapsed ms to a terse human label. Updates ~every 15s so we keep
// seconds precision only for the first minute, switching to m/h after.
const formatElapsed = (ms) => {
  if (ms === null || ms === undefined) return null;
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return 'just opened';
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const LiveTradePanel = ({
  trade,
  darkMode,
  setTrades,
  allTrades = [],
  getConfluenceName,
  // v7.5.x — setup definitions, used to surface the "Recommended Confluences"
  // pinned on the active trade's setup. Optional; degrades to no row when
  // setupDetails is missing or the setup has no recommended chips pinned.
  setupDetails = {},
  onEditTrade,
  // Phase 11.2 additions — mid-trade coaching signals.
  // All props are optional; LiveTradePanel degrades gracefully when the host
  // doesn't supply them (Coach insights just silently stay empty).
  todayTrades = [],
  journalEntry = null,
  allJournalEntries = {},
  getTradeNetPnL,
  getAccountById,
  userTier = 'free',
  // Phase 11.3 — forward-looking event whispers. Today's economic calendar
  // (from journalEntry.newsEvents in JournalView). Optional; the signal just
  // silently stays quiet when this isn't supplied or is empty.
  todayEvents = [],
  // Phase 15 — Real-Time Alerts settings slice. When enabled + a live
  // signal of sufficient severity fires, dispatchAlert routes it to OS
  // notifications + optional Discord webhook. Optional — when omitted
  // or .enabled === false, the panel runs as before with no side effects.
  coachAlerts = null,
}) => {
  /* ---- Notes draft + autosave state -------------------------------- */
  // Keep a local draft so each keystroke doesn't round-trip through the
  // global trades array (would cause visible lag and re-renders on large
  // trade lists). The draft is the source of truth while typing; we
  // debounced-commit to `trade.notes` via setTrades.
  //
  // Lazy initializer — `notesHtmlToPlain` runs once on mount, not every
  // render. Without the function wrapper, React would re-evaluate it on
  // every render even though only the initial value matters.
  const [notesDraft, setNotesDraft] = useState(() => notesHtmlToPlain(trade?.notes || ''));
  // 'idle' | 'dirty' | 'saving' | 'saved' — drives the status badge
  const [saveState, setSaveState] = useState('idle');
  const saveTimerRef = useRef(null);
  const savedResetRef = useRef(null);
  // Captures the 200ms "saved-flash" timer so unmount cleanup can clear it.
  // Without this, a trade closing during the 200ms gap left the timer to
  // fire `setSaveState('saved')` on an unmounted component.
  const flashTimerRef = useRef(null);
  // Textarea DOM ref — used by the sync-back effect to detect focus and
  // preserve cursor position when an external trade.notes update lands
  // while the user is in the field. Without this, React rewriting `value`
  // would yank the cursor to the end mid-thought.
  const textareaRef = useRef(null);
  // Tracks the value we last committed to trade.notes. Used by the sync-back
  // effect to ignore "external" updates that are just our own write echoing
  // back. Without this, a fast type-commit-type-commit cycle could see the
  // first commit's echo arrive AFTER the user has typed more characters,
  // and clobber the in-progress draft.
  // Stored in the same format we WROTE (HTML or plain) — see commitNotes.
  const lastCommittedRef = useRef(trade?.notes || '');
  // Tracks whether trade.notes is currently in HTML form. We sample the
  // initial value, then refresh inside the sync-back effect whenever an
  // external update lands. commitNotes uses this to decide whether to
  // re-wrap the plain-text draft as HTML before writing back.
  const wasHtmlRef = useRef(looksLikeHtml(trade?.notes || ''));

  // Sync draft ← trade.notes when an *external* source updates it (modal edit,
  // confluence-copy merge, etc). Skip while actively typing so we don't clobber.
  // Also skip when external matches our last commit — that's just our own
  // write echoing back through the React tree.
  //
  // Cursor preservation: if the textarea is focused, capture selection BEFORE
  // the value swap and restore it AFTER. React's controlled-input value swap
  // would otherwise jump the cursor to the end of the textarea mid-thought
  // (very disorienting if the user is focused but not actively typing during
  // the saved/idle window when this effect can fire).
  useEffect(() => {
    if (saveState === 'dirty' || saveState === 'saving') return;
    const externalRaw = trade?.notes || '';
    if (externalRaw === lastCommittedRef.current) return; // echo of our own commit
    // External may be HTML (from the rich editor in AddEditTradeModal) or
    // plain text (auto-sync default). Normalize to plain for the textarea
    // and update wasHtmlRef so commits know whether to re-wrap as HTML.
    wasHtmlRef.current = looksLikeHtml(externalRaw);
    const externalPlain = notesHtmlToPlain(externalRaw);
    if (externalPlain === notesDraft) return;

    const ta = textareaRef.current;
    const isFocused = typeof document !== 'undefined' && ta && document.activeElement === ta;
    const selStart = isFocused ? ta.selectionStart : null;
    const selEnd = isFocused ? ta.selectionEnd : null;

    setNotesDraft(externalPlain);

    if (isFocused && selStart != null && selEnd != null) {
      // Restore after React commits the new value (next microtask).
      // Clamp to the new length so we never set a position past the end.
      Promise.resolve().then(() => {
        if (textareaRef.current && document.activeElement === textareaRef.current) {
          const max = externalPlain.length;
          try {
            textareaRef.current.setSelectionRange(
              Math.min(selStart, max),
              Math.min(selEnd, max)
            );
          } catch { /* setSelectionRange can throw on detached nodes */ }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade?.notes]);

  /* ---- Live "X min ago" timer ------------------------------------- */
  // Tick every 15s — we never display seconds past the 1-minute mark, so a
  // finer cadence would just waste renders.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 15 * 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedMs = computeElapsedMs(trade);
  const elapsedLabel = formatElapsed(elapsedMs);

  /* ---- Current time in minutes-from-midnight ---------------------- */
  // Needed by the upcoming-event signal to count down the next high/medium
  // event on today's economic calendar. Computed plainly (not memo'd) — the
  // value is a primitive integer, so React's `===` dep check at the
  // `liveSignals` memo below short-circuits when the minute hasn't rolled.
  // Wrapping this in useMemo([tick]) was a no-op: it ran the body every
  // 15s tick anyway, and the equality short-circuit happens downstream.
  // The `tick` dependency below keeps the value re-derived on each tick.
  // Underscore-prefixed binding satisfies the lint rule.
  const _tickForNowMinutes = tick;
  // ET-anchored "now" — events are timestamped in ET, so the countdown to the
  // next high/medium event must compare against ET wall-clock minutes, not the
  // host clock. For ET users this is identical to local time; for non-ET hosts
  // this fixes a multi-hour offset in the countdown signal.
  const nowMinutes = (() => {
    const { hour, minute } = getEtParts(new Date());
    return hour * 60 + minute;
  })();

  /* ---- Stable refs for getTradeNetPnL / getAccountById ------------ */
  // These callbacks frequently come from inline arrows in the parent (e.g.
  // TradingJournal), so adding them to the liveSignals deps would cause the
  // memo to recompute on every render. We instead capture the LATEST values
  // in refs and read them inside the memo body — the memo only invalidates
  // on data changes, but always uses the current callbacks. The 15s tick
  // is the natural refresh cadence if the caller swaps the fee model
  // (uncommon mid-trade, and a 15s lag for a coaching whisper is fine).
  const getTradeNetPnLRef = useRef(getTradeNetPnL);
  const getAccountByIdRef = useRef(getAccountById);
  useEffect(() => { getTradeNetPnLRef.current = getTradeNetPnL; }, [getTradeNetPnL]);
  useEffect(() => { getAccountByIdRef.current = getAccountById; }, [getAccountById]);

  /* ---- Live Coach insights (mid-trade signals) -------------------- */
  // Recomputes only when the shape of the inputs changes. setTrades churning
  // on autosave does NOT invalidate this — we depend on the stable pieces
  // that computeLiveInsights actually reads on the current trade:
  // id/setup/entryTime/accountId/isOpen. (Notably NOT `confluences` — the
  // signal builders don't consume it, so keeping it here would trigger empty
  // recomputes every time the user toggles a quick-confluence chip below.)
  /* ---- Live bar polling for real-time counter-signals (Phase 19) -- */
  // Polls 5m + 15m bars for the open trade's symbol every 60s. The
  // BarDataService cache makes repeat calls cheap (live-day cache
  // refreshes itself every 60s anyway, so most polls hit cache or
  // trigger one disk read). The bars feed into computeLiveInsights via
  // the new `htfBars` ctx field, which lets `buildRealtimeCounterSignals`
  // detect signals as bars close mid-trade.
  //
  // Stops polling when the trade closes (cleanup via deps). Silent on
  // fetch errors — counter-signal detection is non-critical; we just
  // fall back to the priming whisper.
  //
  // Auto-heal on entry-straddling gaps:
  //   When BarDataService reports a gap in the returned bars and that
  //   gap covers the trade's entryTs, we do ONE cache-bust refetch in
  //   the same tick. This catches the case where the bar source wrote
  //   the missing bar between our last poll and now (the cache window
  //   is up to 60s old; a fresh disk read may reveal the bar that just
  //   showed up). Persistent gaps (file is truly missing the bar) get
  //   the same null-signal behavior — the detector's containment guard
  //   already prevents a false fire, so we just log and move on.
  const [liveHtfBars, setLiveHtfBars] = useState(null);
  useEffect(() => {
    if (!trade?.isOpen || !trade?.symbol || !trade?.date) {
      setLiveHtfBars(null);
      return undefined;
    }
    let cancelled = false;

    // Compute entryTs once per effect run — used by the heal check on
    // every poll. Falls through to skipping the heal if entryTime is
    // missing or unparseable (then we just use whatever bars come back).
    const entryMs = (trade.entryTime && trade.date)
      ? getExecutionInstantMs(trade.date, trade.entryTime)
      : null;
    const entryTs = entryMs ? Math.floor(entryMs / 1000) : null;

    const fetchBarsOnce = async (skipCache = false) => {
      try {
        const [bars5m, bars15m] = await Promise.all([
          barDataService.fetchBars(
            { symbol: trade.symbol, dateStr: trade.date, timeframe: '5m' },
            { skipCache }
          ),
          barDataService.fetchBars(
            { symbol: trade.symbol, dateStr: trade.date, timeframe: '15m' },
            { skipCache }
          ),
        ]);
        return { bars5m, bars15m };
      } catch {
        return { bars5m: null, bars15m: null };
      }
    };

    const pollOnce = async () => {
      let { bars5m, bars15m } = await fetchBarsOnce(false);
      if (cancelled) return;

      // Auto-heal — if any gap in the freshly returned bars straddles
      // the entry, force ONE cache-bust refetch. Skips when entryTs
      // couldn't be resolved (no entryTime, or unparseable). The detector
      // itself still bails safely if the gap persists, so this is purely
      // a recovery optimization for transient bar-write delays.
      if (entryTs != null) {
        const straddling = [];
        for (const tf of [['5m', bars5m], ['15m', bars15m]]) {
          const [label, res] = tf;
          if (!res?.gaps) continue;
          for (const g of res.gaps) {
            if (gapStraddlesTimestamp(g, entryTs)) {
              straddling.push({ tf: label, gap: g });
            }
          }
        }
        if (straddling.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            `[LiveTradePanel] Bar gap straddles trade entryTs=${entryTs}; ` +
            `refetching with cache-bust to recover any newly-arrived bars. Gaps:`,
            straddling
          );
          const refetched = await fetchBarsOnce(true);
          if (cancelled) return;
          bars5m = refetched.bars5m;
          bars15m = refetched.bars15m;

          // After the heal, log whether the gap was actually recovered
          // so persistent gaps are visible (vs. transient ones that
          // self-healed).
          const stillStraddling = [];
          for (const tf of [['5m', bars5m], ['15m', bars15m]]) {
            const [label, res] = tf;
            if (!res?.gaps) continue;
            for (const g of res.gaps) {
              if (gapStraddlesTimestamp(g, entryTs)) {
                stillStraddling.push({ tf: label, gap: g });
              }
            }
          }
          if (stillStraddling.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
              `[LiveTradePanel] Gap persists after cache-bust refetch — ` +
              `bar(s) truly missing from source. Counter-signal detector will ` +
              `bail (no whisper) per containment guard. Gaps:`,
              stillStraddling
            );
          } else {
            // eslint-disable-next-line no-console
            console.log(`[LiveTradePanel] Auto-heal succeeded — gap closed by cache-bust refetch.`);
          }
        }
      }

      const next = {};
      if (bars5m?.bars?.length > 0) next['5m'] = bars5m.bars;
      if (bars15m?.bars?.length > 0) next['15m'] = bars15m.bars;
      setLiveHtfBars(Object.keys(next).length > 0 ? next : null);
    };

    pollOnce();  // immediate first fetch
    const id = setInterval(pollOnce, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [trade?.isOpen, trade?.symbol, trade?.date, trade?.entryTime]);

  const liveSignals = useMemo(() => {
    if (!trade?.isOpen) return [];
    return computeLiveInsights({
      trade,
      allTrades,
      todayTrades,
      todayEvents,
      nowMinutes,
      journalEntry,
      allJournalEntries,
      getTradeNetPnL: getTradeNetPnLRef.current,
      getAccountById: getAccountByIdRef.current,
      userTier,
      // Phase 19 — bars for real-time counter-signal detection
      htfBars: liveHtfBars,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    trade?.id, trade?.setup, trade?.entryTime, trade?.accountId, trade?.isOpen,
    allTrades, todayTrades, todayEvents, nowMinutes,
    journalEntry, allJournalEntries, userTier,
    liveHtfBars,
  ]);

  /* ---- Real-time Alerts (Phase 15) -------------------------------- */
  // Watch for NEW live signals and fire desktop / Discord alerts via the
  // dispatchAlert service. We keep a per-trade "last alerted" set in a
  // ref so re-renders don't re-fire the same signal — only genuinely new
  // signal keys trigger an alert. The dispatcher itself also has a
  // 60s in-memory dedup window as a second-layer guard.
  const lastAlertedKeysRef = useRef(new Set());
  useEffect(() => {
    if (!coachAlerts?.enabled) return;
    if (!Array.isArray(liveSignals) || liveSignals.length === 0) return;
    const newKeys = liveSignals.filter(s => !lastAlertedKeysRef.current.has(s.key));
    if (newKeys.length === 0) return;
    // Mark as alerted FIRST so a second render before the async dispatch
    // resolves doesn't double-fire.
    for (const s of newKeys) lastAlertedKeysRef.current.add(s.key);
    // Map live-signal tone → alert severity. Live signals only have
    // 'warn' | 'success' | 'info' tones; the dispatcher's severity gate
    // expects coach-engine severity strings.
    const toneToSeverity = { warn: 'warning', success: 'success', info: 'info' };
    (async () => {
      // Lazy-import to avoid pulling Electron IPC into the LiveTradePanel
      // initial bundle when alerts are disabled.
      const { dispatchAlert } = await import('../../services/coach/alerts/dispatchAlert');
      for (const sig of newKeys) {
        try {
          await dispatchAlert({
            alert: {
              key: `live:${sig.key}:${trade.id || ''}`,
              title: sig.label || 'Coach signal',
              body: sig.detail || '',
              severity: toneToSeverity[sig.tone] || 'info',
              scope: 'live',
              scopeId: trade.symbol || '',
            },
            settings: coachAlerts,
          });
        } catch { /* dispatcher swallows its own errors; this is just defense in depth */ }
      }
    })();
  }, [liveSignals, coachAlerts, trade?.id, trade?.symbol]);

  /* ---- Top confluences from user history --------------------------- */
  // Learn from the trader's own history rather than shipping a generic list.
  // Falls back to "first N in allConfluences" if the user has no trades yet
  // — handled by the parent passing the raw library when history is empty.
  //
  // Memo signature: trades length + last trade id + last trade's confluence
  // count. This is O(1) per render and re-runs the body only when the user
  // history meaningfully shifts (new trade added, last trade's confluences
  // changed). Editing a confluence on a NON-last trade leaves top-N slightly
  // stale until the next add — acceptable for a "most-used" hint.
  // Without this signature, allTrades's parent-side identity churn (any
  // unrelated TradingJournal re-render) re-iterated the entire history.
  const topConfluenceSig = useMemo(() => {
    if (!Array.isArray(allTrades) || allTrades.length === 0) return '0';
    const last = allTrades[allTrades.length - 1];
    return `${allTrades.length}:${last?.id || ''}:${(Array.isArray(last?.confluences) ? last.confluences.length : 0)}`;
  }, [allTrades]);

  const topConfluenceIds = useMemo(() => {
    if (!Array.isArray(allTrades) || allTrades.length === 0) return [];
    const counts = new Map();
    for (const t of allTrades) {
      const conf = Array.isArray(t.confluences) ? t.confluences : [];
      for (const id of conf) counts.set(id, (counts.get(id) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([id]) => id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topConfluenceSig]);

  /* ---- Recent experiments (Phase 16) ------------------------------ */
  // Surface recently-used experiment names as quick-pick chips so the trader
  // can join an existing test in one click while the trade is live. We scan
  // the back of the history (most recent first), dedup, cap at 5 — the most
  // relevant experiments are the ones actively running, not historical ones.
  // Reuses topConfluenceSig as the change signal: it captures the same
  // "history grew or last trade changed" trigger we care about here.
  const recentExperiments = useMemo(() => {
    if (!Array.isArray(allTrades) || allTrades.length === 0) return [];
    const seen = new Set();
    const out = [];
    for (let i = allTrades.length - 1; i >= 0 && out.length < 5; i--) {
      const name = (allTrades[i]?.experiment || '').trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      out.push(name);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topConfluenceSig]);

  /* ---- Commit draft to the trade ---------------------------------- */
  const commitNotes = (value) => {
    if (!trade?.id || typeof setTrades !== 'function') {
      // This branch is unreachable in normal mounting (parent always wires both),
      // but if it ever fires it'd manifest as "notes don't save" with no UI clue.
      // Log loud so the next debugger doesn't have to grep.
      console.warn('[LiveTradePanel] commitNotes: missing trade.id or setTrades — notes write skipped', { hasTradeId: !!trade?.id, hasSetTrades: typeof setTrades === 'function' });
      return;
    }
    setSaveState('saving');
    // If trade.notes was HTML when we last saw it (rich editor has touched
    // it), preserve that format on commit by re-wrapping our plain-text
    // draft in <p> tags. Otherwise commit as plain text — that's what
    // auto-sync writes by default and the rich editor handles plain fine.
    const toWrite = wasHtmlRef.current ? plainTextToNotesHtml(value) : value;
    setTrades(prev => prev.map(t => (t.id === trade.id ? { ...t, notes: toWrite } : t)));
    // Record what we just wrote so the sync-back effect can ignore the echo
    lastCommittedRef.current = toWrite;
    // Clear any in-flight flash timers from a previous commit
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    if (savedResetRef.current) clearTimeout(savedResetRef.current);
    // Delay "saved" flash so it's visible even on fast writes. Both timers
    // are tracked in refs so unmount cleanup can clear them — without that
    // a trade closing during the 200ms window fires setSaveState on an
    // unmounted component.
    flashTimerRef.current = setTimeout(() => {
      setSaveState('saved');
      flashTimerRef.current = null;
      savedResetRef.current = setTimeout(() => {
        setSaveState('idle');
        savedResetRef.current = null;
      }, 1800);
    }, 200);
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    setNotesDraft(value);
    setSaveState('dirty');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      commitNotes(value);
      saveTimerRef.current = null;
    }, 800);
  };

  const handleNotesBlur = () => {
    // Flush pending debounce on blur so a quick tab-away still saves
    if (saveState === 'dirty') {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      commitNotes(notesDraft);
    }
  };

  // Cleanup on unmount — important when the trade closes mid-typing
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedResetRef.current) clearTimeout(savedResetRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  /* ---- Experiment quick-set (Phase 16) ---------------------------- */
  // Tag/untag this trade as part of an A/B experiment. Empty string clears it.
  // The fuchsia "experiment" badge on the trade card and the
  // experimentComparison analyzer both key off `trade.experiment`.
  const setExperimentValue = (value) => {
    if (!trade?.id || typeof setTrades !== 'function') return;
    const trimmed = (value || '').trim();
    setTrades(prev => {
      const idx = prev.findIndex(t => t.id === trade.id);
      if (idx < 0) return prev;
      // Idempotency: if value didn't actually change, skip the setState to
      // avoid pointless re-renders down the tree.
      if ((prev[idx].experiment || '') === trimmed) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], experiment: trimmed };
      return next;
    });
  };

  /* ---- Confluence chip toggle ------------------------------------- */
  const toggleConfluence = (confId) => {
    if (!trade?.id || typeof setTrades !== 'function') {
      console.warn('[LiveTradePanel] toggleConfluence: missing trade.id or setTrades — toggle skipped', { confId, hasTradeId: !!trade?.id, hasSetTrades: typeof setTrades === 'function' });
      return;
    }
    setTrades(prev => {
      // Bail if the trade isn't in `prev` — without this, .map produces a new
      // array reference even when nothing changed, churning downstream renders.
      const idx = prev.findIndex(t => t.id === trade.id);
      if (idx < 0) return prev;
      const t = prev[idx];
      const curr = Array.isArray(t.confluences) ? t.confluences : [];
      const hasIt = curr.includes(confId);
      const currCounter = Array.isArray(t.counterConfluences) ? t.counterConfluences : [];
      const updated = {
        ...t,
        confluences: hasIt ? curr.filter(c => c !== confId) : [...curr, confId],
        // Enforce mutual exclusivity: a confluence can't be both For and Against
        counterConfluences: currCounter.filter(c => c !== confId)
      };
      const next = prev.slice();
      next[idx] = updated;
      return next;
    });
  };

  /* ---- Completion pips -------------------------------------------- */
  const hasSetup = !!trade?.setup;
  const hasConfluences = Array.isArray(trade?.confluences) && trade.confluences.length > 0;
  // Use draft for Notes pip so it flips green the moment the user types.
  // Fall back to the HTML-stripped trade.notes so an empty Quill paragraph
  // (`<p><br></p>`) doesn't false-positive the pip as "has notes".
  const hasNotes = !!(notesDraft || notesHtmlToPlain(trade?.notes || '')).trim();

  /* ---- Styling ---------------------------------------------------- */
  const panelCls = darkMode
    ? 'bg-gradient-to-br from-yellow-950/30 via-gray-900/60 to-gray-900/40 border border-yellow-500/30 backdrop-blur-sm'
    : 'bg-gradient-to-br from-yellow-50/80 via-white/70 to-white/50 border border-yellow-300';

  const headerLabelCls = darkMode ? 'text-yellow-200/90' : 'text-yellow-800';
  const subLabelCls = darkMode ? 'text-gray-400' : 'text-gray-500';
  const pipOnCls = darkMode ? 'text-emerald-300' : 'text-emerald-700';
  const pipOffCls = subLabelCls;

  const saveStatusText =
    saveState === 'saving' ? 'Saving…'
    : saveState === 'saved' ? 'Saved'
    : saveState === 'dirty' ? 'Unsaved'
    : '';
  const saveStatusCls =
    saveState === 'saved' ? (darkMode ? 'text-green-400' : 'text-green-600')
    : saveState === 'dirty' ? (darkMode ? 'text-amber-300' : 'text-amber-600')
    : (darkMode ? 'text-gray-400' : 'text-gray-500');

  /* ---- Render ----------------------------------------------------- */
  return (
    <div className={`mt-3 rounded-lg px-3 py-2.5 ${panelCls}`}>
      {/* Header: Live timer · completion pips · save state · Full Editor */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider ${headerLabelCls}`}>
            <Activity size={12} className="animate-pulse" aria-hidden="true" />
            {elapsedLabel ? `Live · ${elapsedLabel}` : 'Live'}
          </span>
          <span className="flex items-center gap-2 text-[11px]">
            <span className={`inline-flex items-center gap-1 ${hasSetup ? pipOnCls : pipOffCls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${hasSetup ? 'bg-emerald-400' : (darkMode ? 'bg-gray-600' : 'bg-gray-300')}`} />
              Setup
            </span>
            <span className={`inline-flex items-center gap-1 ${hasConfluences ? pipOnCls : pipOffCls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${hasConfluences ? 'bg-emerald-400' : (darkMode ? 'bg-gray-600' : 'bg-gray-300')}`} />
              Confluences
            </span>
            <span className={`inline-flex items-center gap-1 ${hasNotes ? pipOnCls : pipOffCls}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${hasNotes ? 'bg-emerald-400' : (darkMode ? 'bg-gray-600' : 'bg-gray-300')}`} />
              Notes
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saveStatusText && (
            <span className={`text-[10px] uppercase tracking-wider font-medium ${saveStatusCls}`}>
              {saveStatusText}
            </span>
          )}
          <button
            onClick={() => onEditTrade?.(trade)}
            className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
              darkMode
                ? 'border-gray-600 text-gray-300 hover:border-blue-400 hover:text-blue-200 hover:bg-blue-500/10'
                : 'border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50'
            }`}
            title="Open full editor"
          >
            <Edit3 size={11} />
            Full Editor
            <ArrowUpRight size={10} />
          </button>
        </div>
      </div>

      {/* Notes textarea — plaintext, autosave on idle + blur */}
      <textarea
        ref={textareaRef}
        value={notesDraft}
        onChange={handleNotesChange}
        onBlur={handleNotesBlur}
        placeholder="Log your thesis, emotion, or what you're watching — autosaves as you type."
        aria-label="Live trade notes"
        rows={2}
        className="w-full input-modern resize-y"
      />

      {/* Mid-trade Coach signals — renders nothing when history is thin */}
      <LiveTradeInsights
        signals={liveSignals}
        darkMode={darkMode}
      />

      {/* Recommended confluences — pinned on the active trade's setup via
          Settings → Setups → Edit Setup. Surfaces the chips the trader said
          "this setup should have these" so they're a one-tap apply in the
          live panel. Distinct from Quick Confluences below (those are
          top-N most-used historically across ALL setups). Purple matches
          the Setups tab convention. Hidden when no setup or no pins. */}
      {(() => {
        const setupName = trade?.setup;
        const recIds = (setupName && setupDetails?.[setupName]?.recommendedConfluences) || [];
        if (recIds.length === 0) return null;
        return (
          <div className="mt-2">
            <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${subLabelCls}`}>
              Recommended Confluences
              <span className={`ml-1 normal-case font-normal ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                · pinned on {setupName}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recIds.map(confId => {
                const active = Array.isArray(trade.confluences) && trade.confluences.includes(confId);
                const name = typeof getConfluenceName === 'function' ? getConfluenceName(confId) : confId;
                return (
                  <button
                    key={confId}
                    type="button"
                    onClick={() => toggleConfluence(confId)}
                    aria-pressed={active}
                    aria-label={`${name} confluence — ${active ? 'tagged, click to remove' : 'click to add'}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-all backdrop-blur-sm ${
                      active
                        ? (darkMode
                          ? 'bg-purple-500/25 border-purple-400/60 text-purple-100 ring-1 ring-purple-400/30 shadow-sm shadow-purple-500/20'
                          : 'bg-purple-100 border-purple-400 text-purple-800 ring-1 ring-purple-300/50')
                        : (darkMode
                          ? 'bg-gray-800/50 border-purple-700/40 text-gray-300 hover:border-purple-500/60 hover:text-purple-200'
                          : 'bg-white/60 border-purple-300/60 text-gray-700 hover:border-purple-400 hover:text-purple-700')
                    }`}
                    title={active
                      ? `Click to remove "${name}"`
                      : `Recommended for ${setupName} — click to add`}
                  >
                    {active && <Check size={9} className="inline mr-0.5 -mt-0.5" strokeWidth={3} aria-hidden="true" />}
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Quick confluence chips (top-N from user history) */}
      {topConfluenceIds.length > 0 && (
        <div className="mt-2">
          <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${subLabelCls}`}>
            Quick Confluences
          </div>
          <div className="flex flex-wrap gap-1.5">
            {topConfluenceIds.map(confId => {
              const active = Array.isArray(trade.confluences) && trade.confluences.includes(confId);
              const name = typeof getConfluenceName === 'function' ? getConfluenceName(confId) : confId;
              return (
                <button
                  key={confId}
                  type="button"
                  onClick={() => toggleConfluence(confId)}
                  aria-pressed={active}
                  aria-label={`${name} confluence — ${active ? 'tagged, click to remove' : 'click to add'}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition-all backdrop-blur-sm ${
                    active
                      ? (darkMode
                        ? 'bg-cyan-500/20 border-cyan-400/60 text-cyan-100 ring-1 ring-cyan-400/30 shadow-sm shadow-cyan-500/20'
                        : 'bg-cyan-100 border-cyan-400 text-cyan-800 ring-1 ring-cyan-300/50')
                      : (darkMode
                        ? 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-cyan-500/50 hover:text-cyan-200'
                        : 'bg-white/60 border-gray-300 text-gray-700 hover:border-cyan-400 hover:text-cyan-700')
                  }`}
                  title={active ? 'Click to remove' : 'Click to add'}
                >
                  {active && <Check size={9} className="inline mr-0.5 -mt-0.5" strokeWidth={3} aria-hidden="true" />}
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick experiment-pick (Phase 16). Active state shows the current
          experiment in fuchsia; click to clear. Recent experiments surface as
          quick-pick chips so the trader can join an existing A/B test in one
          click. Inline text input lets them name a new test without leaving
          the live panel. */}
      <div className="mt-2">
        <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${subLabelCls}`}>
          Experiment
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          {trade.experiment ? (
            <button
              type="button"
              onClick={() => setExperimentValue('')}
              aria-label={`Currently in experiment ${trade.experiment} — click to remove`}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-all inline-flex items-center gap-1 ${
                darkMode
                  ? 'bg-fuchsia-500/20 border-fuchsia-400/60 text-fuchsia-100 ring-1 ring-fuchsia-400/30 shadow-sm shadow-fuchsia-500/20'
                  : 'bg-fuchsia-100 border-fuchsia-400 text-fuchsia-800 ring-1 ring-fuchsia-300/50'
              }`}
              title="Click to remove from experiment"
            >
              <span aria-hidden="true">🧪</span>
              <span className="font-medium">{trade.experiment}</span>
              <Check size={9} className="inline -mt-0.5" strokeWidth={3} aria-hidden="true" />
            </button>
          ) : (
            <>
              {recentExperiments.map(name => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setExperimentValue(name)}
                  aria-label={`Tag this trade as part of experiment ${name}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition-all backdrop-blur-sm inline-flex items-center gap-1 ${
                    darkMode
                      ? 'bg-gray-800/50 border-gray-700 text-gray-300 hover:border-fuchsia-500/50 hover:text-fuchsia-200'
                      : 'bg-white/60 border-gray-300 text-gray-700 hover:border-fuchsia-400 hover:text-fuchsia-700'
                  }`}
                  title={`Tag this trade as part of "${name}"`}
                >
                  <span aria-hidden="true">🧪</span>
                  {name}
                </button>
              ))}
              <input
                type="text"
                placeholder={recentExperiments.length > 0 ? 'or new name…' : 'New experiment name…'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const v = e.currentTarget.value.trim();
                    if (v) {
                      setExperimentValue(v);
                      e.currentTarget.value = '';
                    }
                  }
                }}
                aria-label="New experiment name — press Enter to tag"
                className={`text-[11px] px-2 py-0.5 rounded-full border w-44 transition-colors ${
                  darkMode
                    ? 'bg-gray-900/40 border-gray-700 text-gray-200 placeholder-gray-500 focus:border-fuchsia-500/60'
                    : 'bg-white/80 border-gray-300 text-gray-700 placeholder-gray-400 focus:border-fuchsia-400'
                } focus:outline-none focus:ring-1 focus:ring-fuchsia-400/30`}
              />
            </>
          )}
        </div>
      </div>

      {/* Inline setup nudge — only if missing, to avoid adding clutter once set */}
      {!hasSetup && (
        <button
          onClick={() => onEditTrade?.(trade)}
          className={`mt-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-dashed transition-colors ${
            darkMode
              ? 'border-yellow-600/50 text-yellow-300 hover:bg-yellow-900/20'
              : 'border-yellow-400 text-yellow-700 hover:bg-yellow-50'
          }`}
        >
          <Pencil size={11} />
          Tag your setup
        </button>
      )}
    </div>
  );
};

export default LiveTradePanel;
